/*
 * <elexa-announcement-slider> (sections/announcement-slider.liquid).
 * Rotates the bar's messages: the next one slides up from below while the current one leaves
 * upwards ("previous" runs the other way), loops both ways, ignores clicks during a transition.
 * Pauses on hover, while keyboard focus is inside, while the tab is hidden and while a message block is
 * selected in the theme editor. Reduced motion: messages swap instantly, rotation continues.
 * Live region is polite only after the shopper uses the arrows (silent while auto-rotating).
 */
if (!customElements.get('elexa-announcement-slider')) {
  class ElexaAnnouncementSlider extends HTMLElement {
    connectedCallback() {
      if (this.dataset.rotate !== 'true') return;
      this.slides = Array.from(this.querySelectorAll('[data-ex-ann-slide]'));
      if (this.slides.length < 2) return;
      this.track = this.querySelector('.ex-ann__track');
      this.bar = this.closest('.ex-ann') || this;
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.interval = parseInt(this.dataset.interval, 10) || 4000;
      this.speed = parseInt(this.dataset.speed, 10) || 500;
      this.busy = false;
      this.pauses = new Set();

      this.onPrev = () => this.go(-1, true);
      this.onNext = () => this.go(1, true);
      this.onEnter = () => this.pause('hover');
      this.onLeave = () => this.resume('hover');
      // Keyboard focus pauses; a mouse click on an arrow does not keep the bar paused.
      this.onFocusIn = (event) => {
        let keyboard = true;
        try {
          keyboard = event.target.matches(':focus-visible');
        } catch (error) {}
        if (keyboard) this.pause('focus');
      };
      this.onFocusOut = (event) => {
        if (!this.bar.contains(event.relatedTarget)) this.resume('focus');
      };
      this.onVisibility = () => (document.hidden ? this.pause('hidden') : this.resume('hidden'));
      this.onBlockSelect = (event) => {
        const i = this.slides.findIndex((s) => s.dataset.blockId === (event.detail && event.detail.blockId));
        if (i < 0) return;
        this.pause('editor');
        this.show(i, 0, false);
      };
      this.onBlockDeselect = (event) => {
        if (this.slides.some((s) => s.dataset.blockId === (event.detail && event.detail.blockId))) this.resume('editor');
      };

      const prev = this.querySelector('[data-ex-ann-prev]');
      const next = this.querySelector('[data-ex-ann-next]');
      if (prev) prev.addEventListener('click', this.onPrev);
      if (next) next.addEventListener('click', this.onNext);
      this.bar.addEventListener('pointerenter', this.onEnter);
      this.bar.addEventListener('pointerleave', this.onLeave);
      this.bar.addEventListener('focusin', this.onFocusIn);
      this.bar.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.applyArrowColour(this.slides[this.index]);
      if (document.hidden) this.pauses.add('hidden');
      this.schedule();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.doneTimer);
      if (!this.slides || this.slides.length < 2) return;
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    pause(reason) {
      this.pauses.add(reason);
      clearTimeout(this.timer);
    }

    resume(reason) {
      this.pauses.delete(reason);
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.pauses.size) return;
      this.timer = setTimeout(() => this.go(1, false), this.interval);
    }

    go(direction, manual) {
      if (this.busy) return;
      const n = this.slides.length;
      const target = (this.index + direction + n) % n;
      if (manual && this.track) this.track.setAttribute('aria-live', 'polite');
      this.show(target, direction, true);
    }

    show(target, direction, animate) {
      if (target === this.index) return;
      const from = this.slides[this.index];
      const to = this.slides[target];
      this.index = target;
      this.applyArrowColour(to);
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!animate || reduce || !direction) {
        clearTimeout(this.doneTimer);
        this.busy = false;
        this.finish(to);
        this.schedule();
        return;
      }

      this.busy = true;
      const timing = `transform ${this.speed}ms ease`;
      to.style.transition = 'none';
      to.style.transform = `translateY(${direction > 0 ? '100%' : '-100%'})`;
      to.style.visibility = 'visible';
      void to.offsetHeight;
      to.style.transition = timing;
      from.style.transition = timing;
      to.style.transform = 'translateY(0)';
      from.style.transform = `translateY(${direction > 0 ? '-100%' : '100%'})`;
      clearTimeout(this.doneTimer);
      this.doneTimer = setTimeout(() => {
        this.finish(to);
        this.busy = false;
      }, this.speed + 30);
      this.schedule();
    }

    finish(active) {
      this.slides.forEach((slide) => {
        const on = slide === active;
        slide.style.transition = 'none';
        slide.style.transform = '';
        slide.style.visibility = '';
        slide.classList.toggle('is-active', on);
        if (on) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
        slide.inert = !on;
      });
      void active.offsetHeight;
      this.slides.forEach((slide) => (slide.style.transition = ''));
    }

    applyArrowColour(slide) {
      if (slide && slide.dataset.text) this.bar.style.setProperty('--ex-ann-arrow', slide.dataset.text);
      else this.bar.style.removeProperty('--ex-ann-arrow');
    }
  }

  customElements.define('elexa-announcement-slider', ElexaAnnouncementSlider);
}
